import {
  Card,
  CheckIcon,
  FileButton,
  Group,
  Table,
  Text,
  UnstyledButton,
  rem,
} from '@mantine/core';
import dayjs from 'dayjs';
import { dateFormat, dateFormatUI } from '@/index';
import { HiDownload, HiUpload } from 'react-icons/hi';
import { HiTrash } from 'react-icons/hi2';
import { isEmptyForm } from '@/utils/functions';
import { modals } from '@mantine/modals';
import { deleteFetcher, postFetcher } from '@/api/fetchers';
import { notifications } from '@mantine/notifications';
import { useParams } from 'react-router-dom';
import { displayError } from '@/utils/error/errorHandler';

interface AttachmentCardProps {
  serviceId: number;
  formContext: any;
  disabled?: boolean;
}

export const AttachmentCard: React.FC<AttachmentCardProps> = props => {
  const form = props.formContext();
  const attachment = form.values[0];
  const { applicantId } = useParams<{ applicantId: string }>();

  const handleDelete = async (id: number): Promise<void> => {
    try {
      await deleteFetcher(`/applicants/${applicantId}/attachments/${id}`);
    } catch (err) {
      displayError(err);
    }

    form.reset();

    notifications.show({
      message: 'Příloha byla úspěšně smazána.',
      color: 'green',
      icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
    });
  };

  function showConfirmModal(id: number): void {
    modals.openConfirmModal({
      title: 'Smazat přílohu',
      centered: true,
      children: <div>Určitě chcete přílohu smazat? Tato akce je nevratná.</div>,
      labels: { confirm: 'Smazat', cancel: 'Zrušit' },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => {
        void handleDelete(id);
      },
    });
  }

  const handleUpload = async (file: File | null): Promise<void> => {
    if (file) {
      try {
        const formData = new FormData();
        formData.append('Content-Type', 'multipart/form-data');
        formData.append('Accept', 'application/json');
        formData.append('name', file.name);
        formData.append('file', file);
        formData.append('type', 'EMPLOYMENT_CONTRACT');
        formData.append('serviceId', props.serviceId.toString());
        formData.append('signatureDate', dayjs().format(dateFormat));
        formData.append(
          'expirationDate',
          dayjs().add(1, 'year').format(dateFormat)
        );

        const newAttachment = await postFetcher(
          `/applicants/${applicantId}/attachments`,
          formData
        );
        form.reset();
        form.setValues([newAttachment]);

        notifications.show({
          message: 'Příloha byla úspěšně nahrána.',
          color: 'green',
          icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
        });
      } catch (err) {
        displayError(err);
      }
    }
  };

  return (
    <Card shadow="sm" padding="md" radius="md" withBorder>
      <Group align="center" justify="space-between">
        <Text fw={700} size="large">
          Přiložený podepsaný formulář
        </Text>
        {!attachment && (
          <FileButton
            resetRef={() => {}}
            onChange={file => {
              void handleUpload(file);
            }}
            accept=".pdf"
            disabled={props.disabled}
          >
            {p => (
              <UnstyledButton {...p}>
                <HiUpload size={25} color={props.disabled ? 'gray' : 'black'} />
              </UnstyledButton>
            )}
          </FileButton>
        )}
      </Group>
      {!isEmptyForm(form) && attachment && (
        <Table
          verticalSpacing="md"
          style={{
            fontSize: 'medium',
          }}
        >
          <Table.Tbody>
            <Table.Tr key={attachment.id}>
              <Table.Td className="text-left">{attachment.name}</Table.Td>
              <Table.Td className="text-right">
                <Group justify="flex-end">
                  <Text size="sm">Datum konce platnosti:</Text>
                  <Text size="sm">
                    {dayjs(attachment.contract.expireDate).format(dateFormatUI)}
                  </Text>
                  <UnstyledButton
                    onClick={() => {
                      window.open(attachment.url, '_blank');
                    }}
                    data-testid={`open-button-${attachment.id}`}
                  >
                    <HiDownload size={25} />
                  </UnstyledButton>
                  <UnstyledButton
                    onClick={() => showConfirmModal(attachment.id)}
                    disabled={props.disabled}
                    data-testid={`delete-button-${attachment.id}`}
                  >
                    <HiTrash
                      size={25}
                      color={props.disabled ? 'gray' : 'black'}
                    />
                  </UnstyledButton>
                </Group>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
};
